import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Send, 
  MessageSquare,
  Search
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import BottomNav from '@/components/BottomNav';

const MessagesPage = () => {
  const navigate = useNavigate();
  const { recipientId } = useParams();
  const { user, api } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [recipient, setRecipient] = useState(null);
  const messagesEndRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Fetch conversations or messages
  useEffect(() => {
    if (recipientId) {
      fetchMessages();
      fetchRecipient();
    } else {
      fetchConversations();
    }
  }, [recipientId]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/messages');
      setConversations(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/messages/${recipientId}`);
      setMessages(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipient = async () => {
    try {
      const response = await api.get(`/users/${recipientId}`);
      setRecipient(response.data);
    } catch (error) {
      console.error('Error fetching recipient:', error);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const response = await api.post(`/messages/${recipientId}`, {
        content: newMessage.trim()
      });
      setMessages([...messages, response.data]);
      setNewMessage('');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await api.get(`/users/search/${query}`);
      setSearchResults(response.data.filter(u => u.user_id !== user.user_id));
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  // Conversation List View
  if (!recipientId) {
    return (
      <div className="min-h-screen tropic-bg pb-28 md:pb-12 md:pt-16">
        <header className="sticky top-0 md:top-16 z-40 glass border-b">
          <div className="container mx-auto max-w-2xl px-4 py-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate(-1)}
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-xl font-bold">Messages</h1>
            </div>

            {/* Search bar */}
            <div className="mt-4 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un utilisateur..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                data-testid="search-users-input"
              />
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-2 bg-background border border-border rounded-xl overflow-hidden">
                {searchResults.map((searchUser) => (
                  <div
                    key={searchUser.user_id}
                    className="flex items-center gap-3 p-3 hover:bg-muted cursor-pointer border-b border-border last:border-b-0"
                    onClick={() => {
                      navigate(`/messages/${searchUser.user_id}`);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                    data-testid={`search-result-${searchUser.user_id}`}
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={searchUser.picture} />
                      <AvatarFallback>{searchUser.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{searchUser.name}</p>
                      <p className="text-xs text-muted-foreground">{searchUser.commune}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </header>

        <main className="container mx-auto max-w-2xl px-4 py-6">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton h-20 rounded-xl" />
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-16">
              <MessageSquare className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-bold mb-2">Aucune conversation</h3>
              <p className="text-muted-foreground mb-6">
                Commencez à discuter avec d'autres utilisateurs
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {conversations.map((conv) => (
                <Card 
                  key={conv.user_id}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/messages/${conv.user_id}`)}
                  data-testid={`conversation-${conv.user_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={conv.user_picture} />
                          <AvatarFallback>{conv.user_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        {conv.unread_count > 0 && (
                          <span className="absolute -top-1 -right-1 h-5 w-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold truncate">{conv.user_name}</p>
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(conv.last_message_time), { addSuffix: true, locale: fr })}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground truncate mt-1">
                          {conv.last_message}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>

        <BottomNav />
      </div>
    );
  }

  // Chat View
  return (
    <div className="min-h-screen tropic-bg flex flex-col">
      {/* Header */}
      <header className="sticky top-0 md:top-16 z-40 glass border-b">
        <div className="container mx-auto max-w-2xl px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/messages')}
              data-testid="back-to-conversations-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            {recipient && (
              <div 
                className="flex items-center gap-3 cursor-pointer flex-1"
                onClick={() => navigate(`/profile/${recipient.user_id}`)}
              >
                <Avatar className="h-10 w-10">
                  <AvatarImage src={recipient.picture} />
                  <AvatarFallback>{recipient.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{recipient.name}</p>
                  <p className="text-xs text-muted-foreground">{recipient.commune}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Messages */}
      <ScrollArea className="flex-1 px-4 py-4">
        <div className="container mx-auto max-w-2xl space-y-4">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className={`skeleton h-16 rounded-xl ${i % 2 === 0 ? 'w-3/4' : 'w-3/4 ml-auto'}`} />
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-16">
              <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Commencez la conversation !</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div 
                key={msg.message_id}
                className={`flex ${msg.sender_id === user.user_id ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                    msg.sender_id === user.user_id 
                      ? 'bg-primary text-primary-foreground rounded-br-sm' 
                      : 'bg-muted rounded-bl-sm'
                  }`}
                  data-testid={`message-${msg.message_id}`}
                >
                  <p className="text-sm">{msg.content}</p>
                  <p className={`text-xs mt-1 ${
                    msg.sender_id === user.user_id ? 'text-primary-foreground/70' : 'text-muted-foreground'
                  }`}>
                    {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true, locale: fr })}
                  </p>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="sticky bottom-0 glass border-t border-border/50 p-4">
        <div className="container mx-auto max-w-2xl">
          <div className="flex gap-2">
            <Input
              placeholder="Votre message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              disabled={sending}
              className="flex-1"
              data-testid="message-input"
            />
            <Button 
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              data-testid="send-message-btn"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
